import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from 'rtk/query/axiosBaseQuery';
import config from 'config/index';

import { EUserType } from 'types/accessControll.types';

interface dataCollectionContent {
  dataCollectionId: string;
  value: string;
  jsonSchema: {
    type: string;
    description: string;
    enum?: string[];
    dynamicVariable: string;
    constantValue: string;
  };
  rationale: string;
}
type dataCollectionRecord = Record<string, dataCollectionContent>;
type ParsedQuestionAnswer = { iso: string | null; raw: string } | { value: string | boolean; raw: string } | null;

export interface ParsedQuestionItem {
  id: string;
  text: string;
  type: string;
  answer: ParsedQuestionAnswer;
  condition: string;
}

export interface VoiceConversation {
  _id?: string;
  createdAt?: string;
  userId: string;
  conversationId: string;
  eventTimestamp: string;
  agentId: string;
  status: 'done' | 'in_progress';
  callDuration: number;
  isCallPickedUp?: boolean;
  completedAt?: Date;
  parsedAt?: Date;
  questions?: ParsedQuestionItem[];
  languageUsed?: string;
  consentGiven?: boolean;
  channel?: 'phone' | 'chat';
  analysis: {
    transcriptSummary?: string;
    callSummaryTitle: string;
    callSuccessful?: 'success' | 'failure' | 'unknown';
    evaluationCriteriaResults?: Record<
      string,
      {
        criteriaId: string;
        result: 'success' | 'failure';
        rationale: string;
      }
    >;
    dataCollectionResults: {
      questionResults: dataCollectionContent;
    } & dataCollectionRecord;
  };
}

export const reducerPath = 'voiceconversation' as const;
export const path = 'v1/voiceConversations' as const;

export const voiceConversationApi = createApi({
  reducerPath: reducerPath,
  baseQuery: axiosBaseQuery({
    baseUrl: `${config.VITE_BACKEND_URL}/${path}`,
  }),
  tagTypes: ['VoiceConversation'],
  endpoints: (builder) => ({
    getVoiceConversationById: builder.query<VoiceConversation[], string>({
      query: (ref) => {
        return {
          url: `/${ref}`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.HUBERT },
        };
      },
      transformResponse: (resp) => resp.data,
    }),

    reparseVoiceConversation: builder.mutation<VoiceConversation, string>({
      query: (ref) => ({
        url: `/${ref}/reparse`,
        method: 'GET', // Controller defines it as GET
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
      transformResponse: (response: { data: VoiceConversation }) => response.data,
      invalidatesTags: (_result, _error, id) => [{ type: 'VoiceConversation', id }],
    }),
  }),
});

export const {
  // Queries
  useGetVoiceConversationByIdQuery,

  // Mutations
  useReparseVoiceConversationMutation,
} = voiceConversationApi;
